'use client';
import { useId as useNewsletterId } from 'react';
import { CaptchaRequestMessage, isCaptchaValidationError } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useSystemLabels } from '@eliadmin/core/src/hooks/use-system-labels';
import { newsletterSubscribe } from '@eliadmin/core/src/api/newsletter/newsletter-api';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

export function Newsletter() {
  const seoNewsletterId = useNewsletterId();
  const systemLabels = useSystemLabels();
  const { t } = useTranslations();

  // state
  const [email, setEmail] = React.useState('');
  const [checked, setChecked] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [isAlreadySubscribed, setIsAlreadySubscribed] = React.useState(false);

  const subscribeMutation = useMutation({
    mutationFn: newsletterSubscribe,
    onSuccess: () => setIsSubscribed(true),
    onError: (captchaRequestError) => { if (isCaptchaValidationError(captchaRequestError)) { setIsAlreadySubscribed(false); return; } setIsAlreadySubscribed(true); },
  });

  const locale = useLocaleStore((s) => s.currentLocale);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!checked || isSubscribed || !email || subscribeMutation.isPending) return;
    subscribeMutation.mutate({
      locale,
      body: {
        email,
        gdpr: checked,
        return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
      },
    });
  }

  return (
    <section id="newsletters" className={cn('', 'ea-homepage-newsletter')}>
      <form
        className={cn(
          'flex w-full flex-col items-start justify-start',
          'ea-homepage-newsletter__form',
        )}
        onSubmit={handleSubmit}
      >
<CaptchaRequestMessage error={subscribeMutation.error} />
        <div
          className={cn(
            'flex w-full flex-col items-start justify-start gap-4',
            'ea-homepage-newsletter__form-item-email',
          )}
        >
          <div className={cn('flex w-full items-center justify-between gap-4')}>
            <Input
              aria-label={systemLabels('email')}
              id={`newsletter-email-${seoNewsletterId}`}
              type="email"
              name="email"
              placeholder={t('text183')}
              onChange={(e) => setEmail(e.target.value)}
              className={{
                wrapper: cn(
                  'h-[52px] rounded-none border-none bg-transparent',
                  'ea-homepage-newsletter__form-item-email-input',
                ),
                main: cn(
                  'h-[52px] rounded-none bg-white/10 text-sm font-normal text-white/50 placeholder:font-normal placeholder:text-white/20 lg:text-base',
                ),
              }}
            />
            <Button
              type="submit"
              className={cn(
                'h-[52px] border-none bg-white px-6 text-black',
                'ea-homepage-newsletter__form-item-email-submit-button',
              )}
              disabled={email.length === 0 || !checked || isSubscribed}
              isLoading={subscribeMutation.isPending}
            >
              {t('text182')}
            </Button>
          </div>
          {isSubscribed && !isAlreadySubscribed && (
            <span className={cn('font-semibold text-success')}>{t('text3139')}</span>
          )}
          {isAlreadySubscribed && (
            <span className={cn('font-semibold text-success')}>{t('text234')}</span>
          )}
          <div
            className={cn(
              'flex flex-row items-start gap-2',
              'ea-homepage-newsletter__form-item-gdpr',
            )}
          >
            <Checkbox
              checked={checked}
              onCheckedChange={() => setChecked(!checked)}
              id={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn('text-white', 'ea-homepage-newsletter__form-item-gdpr-checkbox')}
              required
            />
            <Label
              htmlFor={`newsletter-gdpr-${seoNewsletterId}`}
              className={cn(
                'text-xs font-normal text-white lg:text-sm',
                'ea-homepage-newsletter__form-item-gdpr-checkbox-label',
              )}
            >
              Prihlásením k odberu novniek súhlasíte so{' '}
              <Link
                prefetch={false}
                prefetchOnHover
                href={ROUTES.GDPR()}
                className="font-bold underline"
              >
                {t('text614')}
              </Link>{' '}
              na marketingové účely.
            </Label>
          </div>
        </div>
      </form>
    </section>
  );
}
